import { Routes, Route, Navigate, NavLink, Link } from 'react-router-dom';

import {
  ShieldCheck,
  Handshake,
  Trophy,
  TrendingUp,
  Building2,
  Users,
  AlertTriangle,
  Scale,
  Lock,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Quote,
  Camera,
  BarChart3,
  Megaphone,
  Compass,
  KeyRound,
  FileCheck2,
  MessageSquare,
  Activity,
  Star,
  XCircle,
} from 'lucide-react';

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#f6f7f9] text-[#050A0F]">
      <div className="mx-auto max-w-7xl px-6 py-6 md:px-10">
        <Tabs />
        {children}
      </div>
    </div>
  );
}

function Tabs() {
  return (
    <div className="mb-10 flex flex-wrap gap-2 rounded-2xl border border-white/80 bg-white/75 p-2 shadow-sm backdrop-blur-xl">
      <Tab to="/diretrizes-rede/manifesto">Manifesto e Cultura</Tab>
      <Tab to="/diretrizes-rede/modelo-operacional">Modelo Operacional</Tab>
      <Tab to="/diretrizes-rede/compliance">Compliance</Tab>
    </div>
  );
}

function Tab({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-xl px-4 py-2 text-sm font-medium transition-all ${
          isActive
            ? 'bg-zinc-950 text-white shadow-sm'
            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function Eyebrow({ children, tone = 'blue' }) {
  const styles =
    tone === 'red'
      ? 'border-red-200 bg-red-50 text-red-600'
      : tone === 'dark'
        ? 'border-white/10 bg-white/10 text-zinc-300'
        : 'border-zinc-200 bg-white/80 text-zinc-500';

  const dot =
    tone === 'red'
      ? 'bg-red-500'
      : tone === 'dark'
        ? 'bg-blue-400'
        : 'bg-blue-600';

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.28em] shadow-sm ${styles}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {children}
    </div>
  );
}

function EditorialTitle({ children, center = true, className = '' }) {
  return (
    <h2
      className={`font-serif text-5xl font-normal tracking-[-0.06em] text-zinc-950 md:text-6xl ${
        center ? 'text-center' : ''
      } ${className}`}
    >
      {children}
    </h2>
  );
}

function SectionHeader({ eyebrow, title, subtitle, tone = 'blue' }) {
  return (
    <div className="mx-auto mb-16 max-w-4xl text-center">
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>

      <EditorialTitle className="mt-6">{title}</EditorialTitle>

      {subtitle && (
        <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-zinc-600">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function ManifestoHero() {
  return (
    <section className="relative isolate overflow-hidden rounded-[2.5rem] border border-zinc-200/70 bg-gradient-to-b from-[#edf5ff] via-white to-white px-8 py-20 shadow-sm md:px-16 md:py-28">
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgba(15,23,42,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.045)_1px,transparent_1px)] bg-[size:32px_32px]" />
      <div className="absolute right-[-120px] top-[-160px] -z-10 h-96 w-96 rounded-full bg-blue-200/70 blur-3xl" />

      <div className="max-w-4xl">
        <Eyebrow>Diretrizes da Rede · Manifesto</Eyebrow>

        <h1 className="mt-8 font-serif text-6xl font-normal tracking-[-0.075em] text-zinc-950 md:text-8xl">
          Manifesto <span className="italic text-blue-600">Elion</span>
        </h1>

        <p className="mt-8 max-w-3xl text-xl leading-9 text-zinc-600 md:text-2xl">
          O futuro do mercado imobiliário será construído por redes de
          confiança, reputação e cooperação — não por corretores isolados.
        </p>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/diretrizes-rede/modelo-operacional"
            className="inline-flex items-center gap-2 rounded-full bg-zinc-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800"
          >
            Como funciona a rede
            <ArrowRight size={16} />
          </Link>

          <Link
            to="/diretrizes-rede/compliance"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-6 py-3 text-sm font-semibold text-zinc-950 shadow-sm transition hover:bg-zinc-50"
          >
            Código de conduta
          </Link>
        </div>
      </div>
    </section>
  );
}

function QuoteCard() {
  return (
    <section className="relative mx-auto -mt-10 max-w-5xl overflow-hidden rounded-[2rem] border border-zinc-200 bg-white p-10 shadow-xl shadow-zinc-200/50 md:p-16">
      <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-blue-100/80 to-transparent" />

      <div className="relative">
        <Quote className="mb-8 text-blue-600" size={38} />

        <p className="max-w-4xl font-serif text-4xl font-normal leading-tight tracking-[-0.06em] text-zinc-950 md:text-6xl">
          Na Elion, ganhar dinheiro nunca deve{' '}
          <span className="italic text-blue-600">destruir a confiança.</span>
        </p>

        <p className="mt-8 max-w-3xl text-base leading-7 text-zinc-600">
          Esta é a diretriz máxima da rede. Tudo o que vem a seguir — pilares,
          mandamentos, compliance e divisão de ganhos — é desdobramento dela.
        </p>
      </div>
    </section>
  );
}

function PillarCard({ icon: Icon, roman, title, children }) {
  return (
    <div className="group relative overflow-hidden rounded-[1.75rem] border border-zinc-200 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-zinc-200/60">
      <span className="absolute right-8 top-7 font-serif text-6xl italic tracking-[-0.08em] text-zinc-100 transition group-hover:text-blue-100">
        {roman}
      </span>

      <div className="relative z-10 mb-8 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
        <Icon size={22} />
      </div>

      <h3 className="relative z-10 text-2xl font-bold tracking-[-0.04em] text-zinc-950">
        {title}
      </h3>

      <p className="relative z-10 mt-5 text-base leading-8 text-zinc-600">
        {children}
      </p>
    </div>
  );
}

function Commandment({ number, children, highlight = false }) {
  return (
    <div
      className={`flex items-center gap-5 rounded-[1.5rem] border bg-white px-6 py-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg ${
        highlight ? 'border-blue-300 ring-1 ring-blue-100' : 'border-zinc-200'
      }`}
    >
      <span className="flex h-9 w-11 shrink-0 items-center justify-center rounded-xl bg-zinc-950 text-sm font-bold text-white">
        {String(number).padStart(2, '0')}
      </span>

      <p className="text-base font-medium leading-7 text-zinc-950">
        {children}
      </p>
    </div>
  );
}

function CinematicFooter({ eyebrow, children }) {
  return (
    <section className="relative isolate -mx-6 overflow-hidden border-y border-zinc-900 bg-[#050A0F] px-8 py-28 text-white md:-mx-10 md:px-16">
      <div className="absolute left-1/2 top-[-260px] -z-10 h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-blue-700/35 blur-3xl" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(11,111,234,0.32),transparent_42%)]" />

      <div className="mx-auto max-w-5xl text-center">
        <Eyebrow tone="dark">{eyebrow}</Eyebrow>

        <p className="mt-10 font-serif text-4xl font-normal leading-tight tracking-[-0.06em] text-white md:text-6xl">
          “{children}”
        </p>
      </div>
    </section>
  );
}

function Manifesto() {
  const mandamentos = [
    'A confiança é a base da profissão.',
    'Reputação demora anos para construir, minutos para destruir.',
    'Negócio bom é o que respeita as duas pontas.',
    'Nenhum corretor é maior que a rede.',
    'Atravessar um colega é atravessar a si mesmo.',
    'Sigilo é palavra dada. Palavra dada é contrato.',
    'Cliente não é commodity. É relação.',
    'Comissão se conquista — nunca se arranca.',
    'Cooperar custa menos que competir mal.',
    'Negócios passam. Reputação permanece.',
  ];

  return (
    <Shell>
      <ManifestoHero />

      <div className="py-24">
        <QuoteCard />
      </div>

      <section className="py-20">
        <SectionHeader
          eyebrow="Os 4 pilares"
          title="O que sustenta a Elion"
          subtitle="Quatro convicções que orientam cada decisão, cada parceria e cada negócio dentro da rede."
        />

        <div className="grid gap-6 md:grid-cols-4">
          <PillarCard icon={ShieldCheck} roman="I" title="Confiança">
            O cliente vem primeiro. Toda relação começa e termina pela confiança
            que conseguimos construir e proteger.
          </PillarCard>

          <PillarCard icon={Handshake} roman="II" title="Cooperação">
            Nenhum corretor cresce sozinho. A rede é o multiplicador. Quem
            coopera, escala. Quem isola, estagna.
          </PillarCard>

          <PillarCard icon={Trophy} roman="III" title="Reputação">
            Reputação é patrimônio. Demora anos para ser construída e minutos
            para ser destruída. Cuidamos dela todos os dias.
          </PillarCard>

          <PillarCard icon={TrendingUp} roman="IV" title="Valorização">
            Valorize seu trabalho. Não há lugar para o desespero, a humilhação
            comercial ou o desconto que destrói o mercado.
          </PillarCard>
        </div>
      </section>

      <section className="border-t border-zinc-200 py-24">
        <SectionHeader
          eyebrow="Os 10 mandamentos"
          title="Os mandamentos da rede"
          subtitle="Frases curtas. Princípios duros. A bússola operacional de todo corretor Elion."
        />

        <div className="grid gap-4 md:grid-cols-2">
          {mandamentos.map((item, index) => (
            <Commandment key={item} number={index + 1} highlight={index === 3}>
              {item}
            </Commandment>
          ))}
        </div>
      </section>

      <CinematicFooter eyebrow="Por que existimos">
        A Elion não foi criada apenas para anunciar imóveis. Foi criada para
        construir um mercado imobiliário mais forte. Porque negócios passam.
        Reputação permanece.
      </CinematicFooter>
    </Shell>
  );
}

function RoleCard({ type, title, badge, icon: Icon, description, items }) {
  return (
    <div className="rounded-[2rem] border border-zinc-200 bg-white p-8 shadow-sm md:p-10">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
            <Icon size={23} />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-zinc-500">
              {type}
            </p>

            <h3 className="mt-1 text-2xl font-bold tracking-[-0.04em] text-zinc-950">
              {title}
            </h3>
          </div>
        </div>

        <span className="rounded-full bg-zinc-950 px-4 py-2 text-xs font-bold text-white">
          {badge}
        </span>
      </div>

      <p className="mt-8 text-base leading-8 text-zinc-600">{description}</p>

      <div className="mt-8 space-y-5">
        {items.map((item) => (
          <div key={item.title} className="flex gap-4">
            <item.icon className="mt-1 shrink-0 text-blue-600" size={18} />

            <div>
              <h4 className="font-bold text-zinc-950">{item.title}</h4>

              <p className="mt-1 text-sm leading-6 text-zinc-600">
                {item.text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueCard({ label, split, badge, rows, justification }) {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-6 border-b border-zinc-200 p-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-zinc-500">
            {label}
          </p>

          <p className="mt-3 font-serif text-5xl tracking-[-0.08em] text-zinc-950">
            {split}
          </p>
        </div>

        <span className="rounded-full bg-zinc-50 px-4 py-2 text-xs font-medium text-zinc-600">
          {badge}
        </span>
      </div>

      <div className="divide-y divide-zinc-200">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid gap-4 px-8 py-5 md:grid-cols-[1fr_auto]"
          >
            <p className="text-zinc-600">{row.label}</p>

            <p
              className={`font-bold ${
                row.highlight ? 'text-blue-600' : 'text-zinc-950'
              }`}
            >
              {row.value}
            </p>
          </div>
        ))}

        <div className="grid gap-4 px-8 py-6 md:grid-cols-[0.6fr_1.4fr]">
          <p className="text-zinc-500">Justificativa</p>

          <p className="text-right text-lg leading-7 text-zinc-600">
            {justification}
          </p>
        </div>
      </div>
    </div>
  );
}

function FlowStep({ icon: Icon, number, title, text, hasArrow }) {
  return (
    <div className="relative rounded-[1.5rem] border border-zinc-200 bg-white p-7 shadow-sm">
      {hasArrow && (
        <ArrowRight
          className="absolute -right-5 top-1/2 hidden -translate-y-1/2 text-zinc-300 md:block"
          size={28}
        />
      )}

      <div className="flex items-start justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
          <Icon size={20} />
        </div>

        <span className="font-serif text-xl italic text-zinc-400">
          {number}
        </span>
      </div>

      <h3 className="mt-8 text-lg font-bold text-zinc-950">{title}</h3>

      <p className="mt-2 text-sm leading-6 text-zinc-600">{text}</p>
    </div>
  );
}

function ModeloOperacional() {
  return (
    <Shell>
      <section className="relative isolate overflow-hidden rounded-[2.5rem] border border-zinc-200 bg-white px-8 py-20 shadow-sm md:px-16 md:py-28">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:34px_34px]" />
        <div className="absolute left-[-120px] top-[-160px] -z-10 h-96 w-96 rounded-full bg-blue-100 blur-3xl" />

        <div className="mx-auto max-w-5xl text-center">
          <Eyebrow>Modelo Operacional</Eyebrow>

          <h1 className="mt-8 font-serif text-6xl font-normal tracking-[-0.075em] text-zinc-950 md:text-8xl">
            Como funciona a{' '}
            <span className="italic text-blue-600">Rede Elion</span>
          </h1>

          <p className="mx-auto mt-8 max-w-3xl text-xl leading-9 text-zinc-600">
            A Elion organiza a colaboração entre quem representa o imóvel e quem
            representa o comprador. Cada ponta tem função, responsabilidade e
            remuneração clara.
          </p>
        </div>
      </section>

      <section className="py-24">
        <SectionHeader
          eyebrow="A dinâmica"
          title="Duas pontas. Um único compromisso."
          subtitle="Na Elion ninguém defende os dois lados da mesa ao mesmo tempo. Essa é a base ética do modelo."
        />

        <div className="grid gap-6 md:grid-cols-2">
          <RoleCard
            type="Listing Agent"
            title="Agente Vendedor"
            badge="Defende quem vende"
            icon={Building2}
            description="Quem angariou o imóvel. Responsável por prepará-lo, posicioná-lo no mercado e proteger os interesses do proprietário do início ao fim."
            items={[
              {
                icon: Camera,
                title: 'Preparação do imóvel',
                text: 'Fotos, vídeo, staging e materiais de apresentação no padrão Elion.',
              },
              {
                icon: BarChart3,
                title: 'Estudo de mercado',
                text: 'Avaliação técnica de preço com base em comparáveis e demanda.',
              },
              {
                icon: Megaphone,
                title: 'Distribuição e captação',
                text: 'Distribuição na rede, portais e gestão dos contatos do vendedor.',
              },
            ]}
          />

          <RoleCard
            type="Buyer’s Agent"
            title="Agente Comprador"
            badge="Defende quem compra"
            icon={Users}
            description="Quem traz o comprador. Faz consultoria ativa, filtra a rede e acompanha o cliente até a assinatura e a entrega das chaves."
            items={[
              {
                icon: Compass,
                title: 'Consultoria ativa',
                text: 'Entende o cliente, filtra a rede e seleciona apenas o que faz sentido.',
              },
              {
                icon: Handshake,
                title: 'Negociação',
                text: 'Defende o comprador na proposta, condições e prazos.',
              },
              {
                icon: KeyRound,
                title: 'Acompanhamento até as chaves',
                text: 'Documentação, due diligence e suporte até a entrega.',
              },
            ]}
          />
        </div>
      </section>

      <section className="border-t border-zinc-200 py-24">
        <SectionHeader
          eyebrow="Comissionamento"
          title="Divisão de ganhos da rede"
          subtitle="Regras claras antes do jogo começar. Quem entrega mais na operação, recebe proporcionalmente."
        />

        <div className="grid gap-6 md:grid-cols-2">
          <RevenueCard
            label="Entre sócios"
            split="30 / 70"
            badge="Modelo padrão"
            rows={[
              {
                label: 'Captação · Listing Agent',
                value: '30%',
                highlight: false,
              },
              {
                label: 'Venda · Buyer’s Agent',
                value: '70%',
                highlight: true,
              },
            ]}
            justification="Maior carga operacional para quem conduz o comprador até o fechamento."
          />

          <RevenueCard
            label="Entre parceiros"
            split="50 / 50"
            badge="Colaboração equilibrada"
            rows={[
              {
                label: 'Captação · Listing Agent',
                value: '50%',
                highlight: false,
              },
              {
                label: 'Venda · Buyer’s Agent',
                value: '50%',
                highlight: false,
              },
            ]}
            justification="Cooperação equilibrada entre redes parceiras, com responsabilidades combinadas antes da venda."
          />
        </div>
      </section>

      <section className="border-t border-zinc-200 py-24">
        <SectionHeader
          eyebrow="Fluxograma"
          title="A operação, da captação às chaves"
        />

        <div className="grid gap-5 md:grid-cols-5">
          <FlowStep
            icon={Building2}
            number="01"
            title="Captação"
            text="Imóvel entra na rede pelo Listing Agent."
            hasArrow
          />

          <FlowStep
            icon={Camera}
            number="02"
            title="Preparação"
            text="Estudo, fotos e materiais no padrão Elion."
            hasArrow
          />

          <FlowStep
            icon={Users}
            number="03"
            title="Match"
            text="Buyer’s Agent ativa o cliente na rede."
            hasArrow
          />

          <FlowStep
            icon={FileCheck2}
            number="04"
            title="Negociação"
            text="Proposta, contraproposta e contrato."
            hasArrow
          />

          <FlowStep
            icon={CheckCircle2}
            number="05"
            title="Fechamento"
            text="Comissionamento e entrega de chaves."
          />
        </div>
      </section>

      <CinematicFooter eyebrow="O modelo da rede">
        O futuro do mercado imobiliário não será construído por corretores
        isolados. Será construído por redes de confiança, reputação e
        cooperação.
      </CinematicFooter>
    </Shell>
  );
}

function RuleMiniCard({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[1.35rem] border border-zinc-200 bg-zinc-50 p-5">
      <Icon className="mb-4 text-blue-600" size={19} />

      <h4 className="font-bold leading-snug text-zinc-950">{title}</h4>

      <p className="mt-3 text-sm leading-6 text-zinc-600">{text}</p>
    </div>
  );
}

function RelationshipQuadrant({ label, title, badge, children }) {
  return (
    <div className="rounded-[2rem] border border-zinc-200 bg-white p-8 shadow-sm md:p-10">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-zinc-500">
            {label}
          </p>

          <h3 className="mt-3 text-3xl font-bold tracking-[-0.05em] text-zinc-950">
            {title}
          </h3>
        </div>

        <span className="rounded-full border border-zinc-200 bg-zinc-50 px-4 py-2 text-xs font-medium text-zinc-600">
          {badge}
        </span>
      </div>

      <div className="grid gap-5 md:grid-cols-2">{children}</div>
    </div>
  );
}

function ForbiddenCard({ title, text, featured = false }) {
  return (
    <div
      className={`rounded-[1.6rem] border bg-white p-7 shadow-sm ${
        featured ? 'border-red-300' : 'border-zinc-200'
      }`}
    >
      <XCircle className="mb-8 text-red-500" size={22} />

      <h3 className="font-bold text-zinc-950">{title}</h3>

      <p className="mt-3 text-base leading-7 text-zinc-600">{text}</p>
    </div>
  );
}

function ReputationMetric({ icon: Icon, title, text }) {
  return (
    <div className="border-b border-zinc-200 p-7 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
      <div className="mb-7 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
        <Icon size={20} />
      </div>

      <h4 className="font-bold text-zinc-950">{title}</h4>

      <p className="mt-3 text-sm leading-6 text-zinc-600">{text}</p>
    </div>
  );
}

function Compliance() {
  return (
    <Shell>
      <section className="relative isolate overflow-hidden rounded-[2.5rem] border border-zinc-200 bg-gradient-to-b from-white via-white to-[#edf5ff] px-8 py-20 shadow-sm md:px-16 md:py-28">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:34px_34px]" />
        <div className="absolute right-[-140px] bottom-[-160px] -z-10 h-96 w-96 rounded-full bg-blue-200 blur-3xl" />

        <div className="mx-auto max-w-5xl text-center">
          <Eyebrow>Compliance e Reputação</Eyebrow>

          <h1 className="mt-8 font-serif text-6xl font-normal tracking-[-0.075em] text-zinc-950 md:text-8xl">
            Código de conduta,{' '}
            <span className="italic text-blue-600">reputação</span> e confiança
          </h1>

          <p className="mx-auto mt-8 max-w-3xl text-xl leading-9 text-zinc-600">
            A Elion não protege apenas imóveis. Protege relações, carteiras,
            clientes, comissões e o nome de quem trabalha certo.
          </p>
        </div>
      </section>

      <section className="py-24">
        <SectionHeader
          eyebrow="Regras de relacionamento"
          title="Dois quadrantes, regras claras"
          subtitle="A forma de se relacionar muda conforme o nível de vínculo com a rede."
        />

        <div className="grid gap-6 md:grid-cols-2">
          <RelationshipQuadrant
            label="Quadrante de sócios"
            title="Entre sócios"
            badge="Vínculo direto com a rede"
          >
            <RuleMiniCard
              icon={Sparkles}
              title="Compartilhamento automático"
              text="Imóveis e oportunidades circulam na rede sem fricção entre sócios."
            />

            <RuleMiniCard
              icon={ShieldCheck}
              title="Respeito à origem do negócio"
              text="Quem captou continua sendo o dono operacional da relação."
            />

            <RuleMiniCard
              icon={Handshake}
              title="Alinhamento prévio obrigatório"
              text="Comissão e papéis acordados antes de qualquer movimento com o cliente."
            />

            <RuleMiniCard
              icon={Lock}
              title="Sigilo absoluto"
              text="Dados, carteira e estratégia comercial são confidenciais por padrão."
            />
          </RelationshipQuadrant>

          <RelationshipQuadrant
            label="Quadrante de parceiros"
            title="Entre parceiros"
            badge="Operação colaborativa"
          >
            <RuleMiniCard
              icon={ShieldCheck}
              title="Respeito integral ao captador"
              text="O Listing Agent é a referência única do imóvel para o parceiro."
            />

            <RuleMiniCard
              icon={Scale}
              title="Comissão acordada antes"
              text="Percentual de divisão definido por escrito antes de iniciar a venda."
            />

            <RuleMiniCard
              icon={Handshake}
              title="Clareza de atendimento"
              text="Quem fala com o cliente, em qual canal e em qual momento."
            />

            <RuleMiniCard
              icon={Lock}
              title="Confidencialidade do comprador"
              text="Identidade do comprador protegida até o ponto comercial certo."
            />
          </RelationshipQuadrant>
        </div>
      </section>

      <section className="border-t border-zinc-200 py-24">
        <div className="mb-16 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <Eyebrow tone="red">Tolerância zero</Eyebrow>

            <h2 className="mt-8 max-w-3xl font-serif text-5xl font-normal leading-none tracking-[-0.07em] text-zinc-950 md:text-7xl">
              Condutas que tiram você da rede.
            </h2>

            <p className="mt-8 max-w-3xl text-lg leading-8 text-zinc-600">
              Não há advertência, nem segunda chance, para o que destrói a
              confiança do mercado.
            </p>
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.22em] text-zinc-500">
            06 condutas proibidas
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          <ForbiddenCard
            title="Atravessamento de negociação"
            text="Excluir parceiro, ocultar cliente ou desviar contato em curso."
          />

          <ForbiddenCard
            title="Anúncios falsos ou fraudulentos"
            text="Preços fictícios, imóveis inexistentes, fotos que não correspondem."
            featured
          />

          <ForbiddenCard
            title="Golpes e fraudes"
            text="Qualquer prática que cause prejuízo material ou jurídico a clientes."
          />

          <ForbiddenCard
            title="Má-fé para reter comissão"
            text="Manobras para esconder ou postergar pagamento devido a parceiros."
          />

          <ForbiddenCard
            title="Comportamento abusivo"
            text="Assédio, discriminação ou tratamento desrespeitoso a clientes ou colegas."
          />

          <ForbiddenCard
            title="Quebra de sigilo"
            text="Compartilhar carteira, dados ou estratégia fora dos limites acordados."
          />
        </div>
      </section>

      <section className="border-t border-zinc-200 py-24">
        <SectionHeader
          eyebrow="O algoritmo do futuro"
          title="Política de reputação"
          subtitle="A Elion mede comportamento. Cada membro carrega indicadores que abrem — ou fecham — portas na rede."
        />

        <div className="overflow-hidden rounded-[2rem] border border-zinc-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-5">
            <ReputationMetric
              icon={Activity}
              title="Histórico de parcerias"
              text="Volume e qualidade de negócios em rede."
            />

            <ReputationMetric
              icon={Star}
              title="Avaliações cruzadas"
              text="Notas de clientes, sócios e parceiros."
            />

            <ReputationMetric
              icon={ShieldCheck}
              title="Índice de confiabilidade"
              text="Cumprimento de acordos e prazos."
            />

            <ReputationMetric
              icon={MessageSquare}
              title="Taxa de resposta"
              text="Velocidade de retorno em oportunidades."
            />

            <ReputationMetric
              icon={Scale}
              title="Comportamento ético"
              text="Aderência ao código de conduta da rede."
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-zinc-200 bg-zinc-50 px-7 py-5">
            <p className="text-sm text-zinc-600">
              Score público da rede · atualizado em tempo real
            </p>

            <span className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-700">
              <span className="h-2 w-2 rounded-full bg-green-600" />
              Reputação saudável
            </span>
          </div>
        </div>
      </section>

      <CinematicFooter eyebrow="O compromisso da rede">
        O futuro do mercado imobiliário pertence aos profissionais que conseguem
        crescer sem destruir confiança.
      </CinematicFooter>
    </Shell>
  );
}

export default function DiretrizesRede() {
  return (
    <Routes>
      <Route index element={<Navigate to="manifesto" replace />} />

      <Route path="manifesto" element={<Manifesto />} />

      <Route path="modelo-operacional" element={<ModeloOperacional />} />

      <Route path="compliance" element={<Compliance />} />
    </Routes>
  );
}
